import React, { useState, useEffect, useCallback } from 'react';
import { LeaderboardResponse } from '../types';
import { api } from '../utils/api';

interface SegmentLeaderboardProps {
  segmentId: number;
  segmentName: string;
  onBack: () => void;
}

const SegmentLeaderboard: React.FC<SegmentLeaderboardProps> = ({ 
  segmentId, 
  segmentName, 
  onBack 
}) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(100);

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getSegmentLeaderboard(segmentId, limit);
      setLeaderboard(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch leaderboard');
    } finally {
      setLoading(false);
    }
  }, [segmentId, limit]);

  useEffect(() => {
    fetchLeaderboard();
  }, [segmentId, limit, fetchLeaderboard]);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow p-6">
        <div className="h-64 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <div className="text-lg mt-4 text-gray-600 dark:text-gray-300">Loading leaderboard...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow p-6">
        <div className="h-64 flex items-center justify-center">
          <div className="text-red-600 text-lg">Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!leaderboard) {
    return null;
  }

  return (
    <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Leaderboard</h2>
            <p className="text-gray-600 text-sm mt-1">
              {segmentName} • {leaderboard.total_results} total results
            </p>
          </div>
          <button
            onClick={onBack}
            className="px-3 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            ← Back to Segments
          </button>
        </div>
      </div>

      <div className="p-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {leaderboard.showing} of {leaderboard.total_results} results
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="limit" className="text-sm text-gray-600">Show:</label>
            <select
              id="limit"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {leaderboard.results.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No results found for this segment yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-4 text-sm font-semibold text-gray-700 uppercase tracking-wider w-24">
                    Rank
                  </th>
                  <th className="px-4 py-4 text-sm font-semibold text-gray-700 uppercase tracking-wider w-48">
                    User
                  </th>
                  <th className="px-4 py-4 text-sm font-semibold text-gray-700 uppercase tracking-wider w-32">
                    Time
                  </th>
                  <th className="px-4 py-4 text-sm font-semibold text-gray-700 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-slate-300 dark:bg-slate-300 divide-y divide-gray-200">
                {leaderboard.results.map((result) => (
                  <tr key={result.id} className="hover:bg-gray-50">
                    <td className="px-4 py-6 whitespace-nowrap">
                      <div className="text-lg font-medium text-gray-900">
                        {result.rank}
                      </div>
                    </td>
                    <td className="px-4 py-6 whitespace-nowrap">
                      <div className="text-lg font-medium text-gray-900">
                        {result.user.username}
                      </div>
                    </td>
                    <td className="px-4 py-6 whitespace-nowrap">
                      <div className="text-lg font-bold text-gray-900">
                        {formatDuration(result.duration_seconds)}
                      </div>
                    </td>
                    <td className="px-4 py-6 whitespace-nowrap">
                      <div className="text-lg text-gray-600">
                        {formatDateTime(result.start_time)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SegmentLeaderboard;
